import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Link, useLocation, useRoutes } from 'react-router-dom';
import styled from 'styled-components';
import { BidSheet } from '../components/bid-sheet/bid-sheet';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { BidButton } from '../components/button/bid-button';
import { Button } from '../components/button/button';
import { Container } from '../components/container/container';
import { FormatAmount } from '../components/format-amount/format-amount';
import { LeaderBoardView } from '../components/leaderboard/leaderboard';
import { LotDetails } from '../components/lot/lot-details';
import { Modal } from '../components/modal/modal';
import { LotNav } from '../components/nav/lot-nav';
import { Skeleton } from '../components/skeleton/skeleton';
import { LeaderboardTable } from '../components/table/leaderboard-table';
import { Tbody } from '../components/table/tbody';
import { Td } from '../components/table/td';
import { Th } from '../components/table/th';
import { Thead } from '../components/table/thead';
import { Tr } from '../components/table/tr';
import { TimeAgo } from '../components/time-ago/time-ago';
import { LotDetailsContainer } from '../containers/lot-details-container';
import type { LotQuery } from '../graphql/types';
import { useMeQuery } from '../graphql/types';
import { device } from '../styles/devices';
import { gutter } from '../styles/mixins';
import { printRank } from '../utils/print-rank';
import { useLotTimer } from '../utils/use-lot-timer';

const Columns = styled.div`
  display: flex;
  flex-direction: row;
  > div {
    flex: 1;
  }
`;

const Sidebar = styled.aside`
  width: 422px;
  margin-left: ${gutter(6)};
  display: none;
  @media ${device.desktop} {
    display: block;
  }
`;

export function LotLeaderboard({
  lot,
  loading,
}: {
  lot?: LotQuery['Lot'];
  loading: boolean;
}) {
  const location = useLocation();
  const me = useMeQuery({
    fetchPolicy: 'cache-first',
  });
  const routes = useRoutes([
    {
      path: '/bid',
      element: <BidSheet lot={lot} />,
    },
  ]);
  const { isEnded } = useLotTimer({
    startsAt: lot?.startsAt,
    endsAt: lot?.endsAt,
  });

  const hasBidOnLeaderboard = lot?.leaderboard.find(
    (item) => item.paddleId === me.data?.me?.paddle?.id
  );

  const hasBidSheetClosed = !location.pathname.includes('/bid');
  const storeItem = localStorage.getItem('hideModal');
  const hasBidded = typeof hasBidOnLeaderboard !== 'undefined';
  const showModalNow = storeItem !== 'true' && hasBidded && hasBidSheetClosed;
  const [showModal, setShowModal] = useState(showModalNow);

  useEffect(() => {
    if (showModalNow) {
      setShowModal(true);
    }
  }, [showModalNow]);

  const year = lot?.startsAt?.substring(0, 4) ?? '';
  const auctionUrl = year.length === 4 ? `/${year}/lots` : '/lots';

  return (
    <Container>
      <LotNav
        left={
          <Breadcrumbs>
            <Link to={auctionUrl}>Auction</Link>
            <Link to={`/lot/${lot?.id}`}>Lot {lot && lot?.number}</Link>
            <p>Leaderboard</p>
          </Breadcrumbs>
        }
        right={
          <Button
            variant="secondary"
            size="sm"
            as={Link}
            to={window.location.pathname.replace(/\/leaderboard.*/, '')}
          >
            Back
          </Button>
        }
        hideDesktop
      />
      <Columns>
        <LeaderBoardView
          action={<BidButton startsAt={lot?.startsAt} endsAt={lot?.endsAt} />}
          hasBid={hasBidded}
        >
          <LeaderboardTable>
            <Thead>
              <Tr>
                <Th>Place</Th>
                <Th>Paddle</Th>
                <Th>Time</Th>
                <Th>Amount</Th>
              </Tr>
            </Thead>
            {!loading && lot ? (
              <Tbody>
                {(lot.leaderboard || []).map((rank) => (
                  <Tr
                    key={rank.id}
                    isActive={rank.paddleId === me.data?.me?.paddle?.id}
                  >
                    <Td>{printRank(rank.rank)}</Td>
                    <Td>#{rank.paddleNumber}</Td>
                    <Td>
                      <TimeAgo value={Date.parse(rank.createdAt)} />
                    </Td>
                    <Td>
                      <FormatAmount
                        value={rank.amount}
                        buyersPremiumPercentage={
                          isEnded
                            ? me?.data?.activeAuction?.buyersPremiumPercentage
                            : undefined
                        }
                        overheadPremiumPercentage={
                          isEnded
                            ? me?.data?.activeAuction?.overheadPremiumPercentage
                            : undefined
                        }
                      />
                    </Td>
                  </Tr>
                ))}
                {Array.from({
                  length: 10 - (lot.leaderboard || []).length,
                }).map((n, i) => (
                  <Tr key={i}>
                    <Td colSpan={6} placeholder="true">
                      {printRank((lot.leaderboard || []).length + i + 1)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            ) : (
              <Tbody>
                {Array.from({
                  length: 10,
                }).map((n, i) => (
                  <Tr key={i}>
                    <Td placeholder="true">{printRank(i + 1)}</Td>
                    <Td>
                      <Skeleton height={19} width={25} visible />
                    </Td>
                    <Td>
                      <Skeleton height={19} width={85} visible />
                    </Td>
                    <Td>
                      <Skeleton height={19} width={85} visible />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            )}
          </LeaderboardTable>
        </LeaderBoardView>
        <Sidebar>
          {!loading && lot ? (
            <LotDetailsContainer
              lot={lot as any}
              cardOnly={true}
              actions={
                <Button
                  as={Link}
                  to={`/lot/${lot.id}`}
                  variant="secondary"
                  fullWidth
                >
                  Back to lot
                </Button>
              }
              isLotClosed={isEnded}
              buyersPremiumPercentage={
                me?.data?.activeAuction?.buyersPremiumPercentage
              }
              overheadPremiumPercentage={
                me?.data?.activeAuction?.overheadPremiumPercentage
              }
            />
          ) : (
            <LotDetails
              isLoading={true}
              brandName=""
              title=""
              summary=""
              highestBid={9}
            />
          )}
        </Sidebar>
      </Columns>
      <AnimatePresence initial={false}>
        {showModal && (
          <Modal
            title="Welcome to the Leaderboard"
            onClose={() => setShowModal(false)}
            onMount={() => localStorage.setItem('hideModal', 'true')}
            action={
              <Button as={Link} to="/my-lots">
                Go to My Lots
              </Button>
            }
          >
            <p>
              Thank you for placing a bid. Your bid is among the ten bids on the
              leaderboard. The top ten bids will win a case of the wine. Good
              luck!
            </p>
          </Modal>
        )}
      </AnimatePresence>
      <AnimatePresence exitBeforeEnter>{routes}</AnimatePresence>
    </Container>
  );
}
